// Over-the-shoulder camera: tight in hover and on foot, pulled back and wide in traversal
// flight (FLIGHT_DESIGN §0.5). Never a child of the cowboy; collides with the world; shake
// uses a trauma model so stacked hits don't explode.
//
// Collision is a cheap sphere-cast: a centre ray plus four rays offset by PROBE_R in the
// camera's right/up plane, from the shoulder pivot back toward the wanted position, against
// world boxes, cylinders and terrain, plus robot body/head spheres (an origin already inside a robot's
// sphere counts as a hit when heading deeper). The shoulder offset itself is probed (world + robots) from the head first so the pivot can't start inside a wall. The boom snaps in at
// once and eases back out. When it is pulled in tight the local cowboy is hidden
// (`hideLocal`, read by the game into CowboyRS.hidden). Allocation-free per frame.

import * as THREE from 'three';
import { raySphere } from '../sim/core';
import { ENEMIES } from '../sim/defs';
import { Move } from '../sim/player';
import type { NetEnemy } from '../sim/protocol';
import { World, RayHit, NO_GROUND } from '../sim/world';

/** sphere-cast radius: comfortably wider than the near-plane corners (~0.14 m at fov 70) */
const PROBE_R = 0.3;
/** gap kept between the camera centre and whatever it hit */
const PAD = 0.32;
/** hide the local cowboy below this boom length (re-show above HIDE_OFF) */
const HIDE_ON = 0.95, HIDE_OFF = 1.15;
/** extra clearance round robot spheres */
const ENEMY_PAD = 0.3;
/** camera-only growth of each robot's body sphere (by EType) where the rendered model overhangs the sim sphere:
 *  the Brute's pauldrons reach ~1.36 m out at shoulder height (sim sphere ~0.97 there), the Metal Brute is the
 *  same boiler at 1.6x (~2.05 m vs ~1.6 m). */
const CAM_BODY_EXTRA: readonly number[] = [0, 0, 0.3, 0, 0, 0.5, 0, 0];
/** ease-back rate (1/s) after an obstruction clears */
const EASE_OUT = 3.5;

const wrapA = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));

export class CameraRig {
  readonly cam: THREE.PerspectiveCamera;
  private dist = 3.7;
  private side = 0.75;
  private up = 0.35;
  private fov = 70;
  private trauma = 0;
  private hit: RayHit = { t: 0, kind: 0, nx: 0, ny: 0, nz: 0 };
  private pivot = new THREE.Vector3();
  private fwd = new THREE.Vector3();
  private right = new THREE.Vector3();
  private upv = new THREE.Vector3();
  private time = 0;
  private colDist = 3.7;
  private colSide = 0.75;
  private enemies: readonly NetEnemy[] = [];
  private enemyCount = 0;
  baseFov = 70;
  /** boom length multiplier (the saloon car pulls it in) */
  distMul = 1;
  lookAtSky = 0;
  /** true while the boom is pulled in so tight the local cowboy would fill the lens */
  hideLocal = false;
  private boomHide = false;
  /** first-person ADS (weapons/viewmodel.ts): set each frame by the game; the lens pushes into the eye */
  fps = false;
  /** 0..1 how far the lens has pushed into the eye */
  fpsW = 0;
  /** eye height over the feet for the first-person lens */
  eyeY = 1.6;
  /** extra zoom (degrees off the ADS fov) once the lens is in the eye: a scope's magnification */
  fpsZoom = 0;

  constructor(cam: THREE.PerspectiveCamera) {
    this.cam = cam;
  }

  /** accessibility: scales every shake (settings 'Screen shake') */
  shakeScale = 1;
  shake(amount: number) { this.trauma = Math.min(1, this.trauma + amount * this.shakeScale); }

  // ---- view punch (shooting feel, client/feel): a damped spring per axis, kicked by impulses.
  // Rotates the lens only, after lookAt, so the aim the tick records is never moved by it.
  private pP = 0; private pPv = 0; // pitch (rad, + = up)
  private pY = 0; private pYv = 0; // yaw
  private pR = 0; private pRv = 0; // roll
  private pF = 0; private pFv = 0; // fov (deg, + = wider)
  private pW = 26; // spring stiffness (rad/s natural frequency), set per punch
  /** kick the lens: peak radians of pitch / yaw / roll and peak fov degrees, reached 1/w s later; w = snap-back rate */
  punch(pitch: number, yaw: number, roll: number, fov: number, w = 26) {
    const k = w * Math.E; // a critically damped spring kicked with v peaks at v / (w e)
    this.pPv += pitch * k; this.pYv += yaw * k; this.pRv += roll * k; this.pFv += fov * k;
    this.pW = Math.max(8, Math.min(60, w));
  }
  private stepPunch(dt: number) {
    // critically damped: x'' = -w^2 x - 2w x'. Sub-stepped so a 30 fps frame stays stable.
    const w = this.pW, n = dt > 1 / 90 ? 3 : 1, h = dt / n;
    for (let i = 0; i < n; i++) {
      this.pPv += (-w * w * this.pP - 2 * w * this.pPv) * h; this.pP += this.pPv * h;
      this.pYv += (-w * w * this.pY - 2 * w * this.pYv) * h; this.pY += this.pYv * h;
      this.pRv += (-w * w * this.pR - 2 * w * this.pRv) * h; this.pR += this.pRv * h;
      this.pFv += (-w * w * this.pF - 2 * w * this.pFv) * h; this.pF += this.pFv * h;
    }
  }

  /** set each frame by the game: sprinting widens the lens a touch so speed reads */
  sprint = false;
  /** landing dip: a damped spring on the pivot height, kicked by land() */
  private dipY = 0;
  private dipV = 0;
  /** a landing at `speed` m/s: the boom sags and springs back (a hero landing sags hard) */
  land(speed: number) { this.dipV -= Math.min(6, speed * 0.25); }
  private dipAcc = 0;
  private flyW = 0;
  private lagX = 0; private lagY = 0; private lagZ = 0; private lagInit = false;
  private prevYaw = 0;
  private roll = 0;

  /** Robots the boom must stay out of (body + head spheres). The array is read, never kept past update. */
  setEnemies(list: readonly NetEnemy[], count: number) { this.enemies = list; this.enemyCount = count; }

  /** Nearest hit of a ray against the world and robot spheres, or maxT. Hits closer than `ignoreBelow` (origin
   * already inside something) are ignored so an offset probe that starts in the ground can't collapse the boom. */
  private cast(world: World, ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number, ignoreBelow: number): number {
    world.raycast(ox, oy, oz, dx, dy, dz, maxT, this.hit);
    let best = this.hit.kind && this.hit.t >= ignoreBelow ? this.hit.t : maxT;
    for (let i = 0; i < this.enemyCount; i++) {
      const e = this.enemies[i];
      const d = ENEMIES[e.type];
      if (!d) continue;
      const ex = e.x - ox, ez = e.z - oz;
      if (ex * ex + ez * ez > (maxT + 3) * (maxT + 3)) continue;
      const tb = this.robotHit(ox, oy, oz, dx, dy, dz, e.x, e.y + d.bodyY, e.z, d.radius + (CAM_BODY_EXTRA[e.type] ?? 0) + ENEMY_PAD);
      if (tb < best) best = tb;
      const th = this.robotHit(ox, oy, oz, dx, dy, dz, e.x, e.y + d.headY, e.z, d.headR + ENEMY_PAD);
      if (th < best) best = th;
    }
    return best;
  }

  /** Ray vs a robot's clearance sphere. An origin already inside the sphere (a robot pressed against the cowboy)
   *  is a hit at 0 when the ray heads deeper in, so the boom collapses instead of passing through the body
   *  (raySphere reports that case as 0, which a `t > 0` test used to throw away); heading out, it's a miss. */
  private robotHit(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, cx: number, cy: number, cz: number, r: number): number {
    const t = raySphere(ox, oy, oz, dx, dy, dz, cx, cy, cz, r);
    if (t > 0) return t;
    if (t < 0) return Infinity;
    return (cx - ox) * dx + (cy - oy) * dy + (cz - oz) * dz > 0 ? 0 : Infinity;
  }

  update(dt: number, world: World, px: number, py: number, pz: number, yaw: number, pitch: number, move: number, speed: number, aiming: boolean, downed: boolean) {
    this.time += dt;
    const flying = move === Move.Flight;
    // flight feel (REF_ANTHEM_FLIGHT §6): the boom trails the body a touch and the horizon banks into turns
    this.flyW += ((flying ? 1 : 0) - this.flyW) * (1 - Math.exp(-dt * 4));
    const tau = 0.002 + 0.08 * this.flyW;
    if (!this.lagInit) { this.lagX = px; this.lagY = py; this.lagZ = pz; this.lagInit = true; }
    const kl = 1 - Math.exp(-dt / tau);
    this.lagX += (px - this.lagX) * kl; this.lagY += (py - this.lagY) * kl; this.lagZ += (pz - this.lagZ) * kl;
    // a big jump (respawn, warp) snaps instead of trailing across the map
    if (Math.abs(px - this.lagX) + Math.abs(py - this.lagY) + Math.abs(pz - this.lagZ) > 6) { this.lagX = px; this.lagY = py; this.lagZ = pz; }
    px = this.lagX; py = this.lagY; pz = this.lagZ;
    const yawRate = dt > 0 ? wrapA(yaw - this.prevYaw) / dt : 0;
    this.prevYaw = yaw;
    const rollT = Math.max(-0.09, Math.min(0.09, -yawRate * 0.03)) * this.flyW;
    this.roll += (rollT - this.roll) * (1 - Math.exp(-dt / 0.15));
    const tDist = (flying ? 6.2 + Math.min(3, speed * 0.06) : downed ? 4.6 : aiming ? 2.4 : move === Move.Hover ? 3.4 : 3.7) * this.distMul;
    const tSide = flying ? 0 : aiming ? 0.85 : 0.75;
    const tUp = flying ? 0.9 : 0.35;
    const tFov = (flying ? this.baseFov + 12 + Math.min(10, speed * 0.25) : aiming ? this.baseFov - 14 - this.fpsZoom * this.fpsW : this.sprint ? this.baseFov + 6 : this.baseFov);
    // the landing dip: a spring (w 14, zeta 0.55: one visible bounce) in fixed 1/240 s sub-steps, so it reads
    // the same at 30 and 240 fps
    this.dipAcc += dt;
    while (this.dipAcc >= 1 / 240) {
      this.dipAcc -= 1 / 240;
      this.dipV += (-this.dipY * 196 - this.dipV * 15.4) / 240;
      this.dipY += this.dipV / 240;
    }
    const k = 1 - Math.exp(-dt * (flying ? 3 : 9));
    this.dist += (tDist - this.dist) * k;
    this.side += (tSide - this.side) * k;
    this.up += (tUp - this.up) * k;
    // ADS zooms in ~0.2 s (Destiny-quick); flight and sprint FOV breathe slower
    this.fov += (tFov - this.fov) * (1 - Math.exp(-dt * (aiming || this.fov < this.baseFov - 1 ? 13 : 6)));
    const ease = 1 - Math.exp(-dt * EASE_OUT);

    const cp = Math.cos(pitch);
    this.fwd.set(-Math.sin(yaw) * cp, Math.sin(pitch), -Math.cos(yaw) * cp);
    this.right.set(Math.cos(yaw), 0, -Math.sin(yaw));
    this.upv.crossVectors(this.right, this.fwd); // camera up (unit: right ⟂ fwd)

    // ---- shoulder: probe from the head out to the shoulder offset so the pivot never starts in a wall
    const hx = px, hy = py + 1.62 + this.up + this.dipY, hz = pz;
    let wantSide = this.side;
    if (this.side > 0.01) {
      const sgn = this.side >= 0 ? 1 : -1;
      const reach = Math.abs(this.side) + PROBE_R;
      const ts = this.cast(world, hx, hy, hz, this.right.x * sgn, 0, this.right.z * sgn, reach, 0);
      if (ts < reach) wantSide = sgn * Math.max(0, ts - PROBE_R);
    }
    this.colSide = wantSide < this.colSide ? wantSide : this.colSide + (wantSide - this.colSide) * ease;
    this.pivot.set(hx, hy, hz).addScaledVector(this.right, this.colSide);

    // ---- boom: sphere-cast (centre + 4 offset rays) from the pivot back toward the wanted spot
    const bx = -this.fwd.x, by = -this.fwd.y, bz = -this.fwd.z;
    const reach = this.dist + PAD;
    let t = this.cast(world, this.pivot.x, this.pivot.y, this.pivot.z, bx, by, bz, reach, 0);
    for (let i = 0; i < 4; i++) {
      const sr = i === 0 ? PROBE_R : i === 1 ? -PROBE_R : 0;
      const su = i === 2 ? PROBE_R : i === 3 ? -PROBE_R : 0;
      const ox = this.pivot.x + this.right.x * sr + this.upv.x * su;
      const oy = this.pivot.y + this.right.y * sr + this.upv.y * su;
      const oz = this.pivot.z + this.right.z * sr + this.upv.z * su;
      const ti = this.cast(world, ox, oy, oz, bx, by, bz, t, 0.06);
      if (ti < t) t = ti;
    }
    // no hard minimum: a wall right behind the pivot collapses the boom onto it rather than pushing the
    // lens into the wall; the local cowboy is hidden below HIDE_ON so the lens never sits inside him
    const want = t < reach ? Math.max(0.02, Math.min(this.dist, t - PAD)) : this.dist;
    this.colDist = want < this.colDist ? want : this.colDist + (want - this.colDist) * ease;

    this.cam.position.set(this.pivot.x + bx * this.colDist, this.pivot.y + by * this.colDist, this.pivot.z + bz * this.colDist);
    // keep out of the terrain, but only when the cowboy is above it: when they've fallen past the island
    // edge, lifting the camera to the island top would teleport it through the cliff
    const g = world.terrain(this.cam.position.x, this.cam.position.z);
    if (g !== NO_GROUND && this.cam.position.y < g + 0.4) {
      const gp = world.terrain(this.pivot.x, this.pivot.z);
      if (gp === NO_GROUND ? this.pivot.y > g : this.pivot.y > gp) this.cam.position.y = g + 0.4;
    }

    if (this.boomHide ? this.colDist > HIDE_OFF : this.colDist < HIDE_ON) this.boomHide = !this.boomHide;
    // first-person ADS: the lens rides the boom in to the eye (~0.15 s in, a touch quicker out), so
    // L2 reads as leaning into the sights rather than a cut
    this.fpsW += ((this.fps ? 1 : 0) - this.fpsW) * (1 - Math.exp(-dt * (this.fps ? 16 : 20)));
    if (this.fpsW < 0.002) this.fpsW = 0;
    if (this.fpsW > 0) {
      const k = this.fpsW * this.fpsW * (3 - 2 * this.fpsW);
      this.cam.position.x += (px - this.cam.position.x) * k;
      this.cam.position.y += (py + this.eyeY + this.dipY - this.cam.position.y) * k;
      this.cam.position.z += (pz - this.cam.position.z) * k;
    }
    this.hideLocal = this.boomHide || this.fpsW > 0.35;

    this.cam.lookAt(this.cam.position.x + this.fwd.x, this.cam.position.y + this.fwd.y, this.cam.position.z + this.fwd.z);
    if (Math.abs(this.roll) > 1e-4) this.cam.rotateZ(this.roll);
    if (this.trauma > 0) {
      const s = this.trauma * this.trauma;
      const tt = this.time * 40;
      this.cam.rotation.x += Math.sin(tt * 1.3) * 0.025 * s;
      this.cam.rotation.y += Math.sin(tt * 1.7 + 2) * 0.025 * s;
      this.cam.rotation.z += Math.sin(tt * 2.1 + 4) * 0.02 * s;
      this.trauma = Math.max(0, this.trauma - dt * 1.6);
    }
    this.stepPunch(Math.min(dt, 0.1));
    if (this.pP !== 0 || this.pY !== 0 || this.pR !== 0) {
      this.cam.rotateX(this.pP); this.cam.rotateY(this.pY); this.cam.rotateZ(this.pR);
    }
    const fov = this.fov + this.pF;
    if (Math.abs(this.cam.fov - fov) > 0.01) { this.cam.fov = fov; this.cam.updateProjectionMatrix(); }
  }
}
